// mds-dojo — dojo/Evented — FAITHFUL form: a plain constructor with on/emit on its
// prototype (matching references/dojo/Evented.js), so it works as a base in the
// faithful declare() C3 chain (e.g. more/_WidgetBaseLite's bases list). c3mro
// treats a plain constructor as a single base and chain-calls its (empty) ctor.
//
// Semantics unchanged from the previous factory form:
//   on(type, listener)  → aspect.after(this, 'on'+type, listener, true)
//   emit(type, ...args) → on.emit(this, type, ...) → calls this['on'+type]
//
// (Was previously authored as a markMixinFactory for the compose() world; the only
// consumer is _WidgetBaseLite, which now uses faithful declare — see the
// dijit-dojox faithful-port migration.)
import aspect from './aspect';
import on from './on';

function Evented(this: any) { /* no-op ctor; state added by subclasses */ }

Evented.prototype.declaredClass = 'dojo.Evented';

Evented.prototype.on = function (this: any, type: string, listener: Function): { remove: () => void } {
  return (on as any).parse(this, type, listener, (target: any, t: string) =>
    aspect.after(target, 'on' + t, listener, true),
  );
};

Evented.prototype.emit = function (this: any, type: string, ...args: any[]): any {
  return (on as any).emit(this, type, args.length <= 1 ? args[0] : args);
};

// Compat typing for the default export. Runtime is the faithful plain ctor above.
// Typed as a constructor that is ALSO zero-arg callable returning itself, so the
// compose-era codemod output resolves both ways it references dojo/Evented:
//   compose(Evented)                          → the CONSTRUCT signature (compose's
//                                                `T extends Ctor` spine overload)
//   InstanceType<ReturnType<typeof Evented>>  → the CALL signature (lite-era mixin-
//                                                factory classification, e.g.
//                                                moreCore/Tools/EntityStore).
// (compose() mixes the plain ctor into a chain via faithful declare at runtime —
// see the plain-constructor branch in dojo/_base/declare.ts.)
export interface EventedInstance {
  declaredClass: string;
  on(type: string, listener: Function): { remove: () => void };
  emit(type: string, ...args: any[]): any;
}
export interface EventedCtor {
  new (): EventedInstance;
  (): EventedCtor;
  prototype: EventedInstance;
}

export default (Evented as unknown as EventedCtor);

// Back-compat named export for any compose() call site that still wants a factory
// form: layer Evented's on/emit onto a base class.
export function EventedMixin<T extends new (...a: any[]) => any>(Base: T): T {
  const C: any = class extends (Base as any) {
    on(type: string, listener: Function) { return (Evented.prototype as any).on.call(this, type, listener); }
    emit(type: string, ...args: any[]) { return (Evented.prototype as any).emit.apply(this, [type, ...args]); }
  };
  return C;
}
